import { Box, Divider } from '@mui/material';
import React from 'react';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { useNavigateBlocker } from 'src/hooks/navigation/useNavigateBlocker';
import { lang, useUserRole } from 'src/utils';

import {
  CancelButton,
  DaypartLabel,
  DaypartValue,
  DriveThruDirection,
  FooterActionsBox,
  PageContainer,
  SaveButton,
  ScrollSectionBox,
  SelectionBox,
  TopHeader,
} from './CustomerTriggeredEventsPage.styled';
import { areAllRowsValid } from './hooks/useCustomerTriggeredEventsPage.helper';
import { useCustomerTriggeredEventsPage } from './hooks/useCustomerTriggeredEventsPage.hook';
import { CustomerTriggeredEventsTable } from './modules/CustomerTriggeredEventsTable';
import { ScreenConfigSection } from './modules/ScreenConfigSection';

export const CustomerTriggeredEventsPage: React.FC = () => {
  const {
    rows,
    setRows,
    handleSaveConfirm,
    customerTriggeredEventsModal,
    publicationModal,
    dayparts,
    handleCancel,
    projectConfig,
    globalComponentsScreensState,
    handleGlobalComponentsScreensChange,
    isSaveLoading,
    transformDaypartComponentsPayload,
    hasChanged,
  } = useCustomerTriggeredEventsPage();

  useNavigateBlocker(hasChanged);

  const { isViewOnly } = useUserRole();

  return (
    <PageContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TopHeader variant="h5">
          {lang.customerTriggeredEventsPage.customerTriggeredEvents}
        </TopHeader>
        {isViewOnly && <ViewOnlyAlert />}
      </Box>

      <ScrollSectionBox>
        <SelectionBox>
          <DaypartLabel variant="body2">
            {lang.customerTriggeredEventsPage.selectComponentForDayparts}
          </DaypartLabel>
          <Divider />
          <DaypartValue variant="body1">{lang.outdoor}</DaypartValue>
        </SelectionBox>

        <CustomerTriggeredEventsTable
          rows={rows}
          setRows={setRows}
          dayparts={dayparts}
          isViewOnly={isViewOnly}
        />

        <SelectionBox>
          <DaypartLabel variant="body2">
            {lang.customerTriggeredEventsPage.selectScreenConfiguration}
          </DaypartLabel>
          <Divider />
          <DriveThruDirection variant="body2">
            {lang.customerTriggeredEventsPage.driveThruDirection(
              projectConfig?.codPosition,
            )}
          </DriveThruDirection>
        </SelectionBox>

        <ScreenConfigSection
          projectConfig={projectConfig}
          globalComponentsScreensState={globalComponentsScreensState}
          handleChange={handleGlobalComponentsScreensChange}
        />
      </ScrollSectionBox>

      {!isViewOnly && (
        <FooterActionsBox>
          <CancelButton variant="text" onClick={handleCancel}>
            {lang.customerTriggeredEventsPage.cancel}
          </CancelButton>
          <SaveButton
            variant="contained"
            onClick={() => customerTriggeredEventsModal.onOpenModal()}
            disabled={!areAllRowsValid(rows, dayparts.length) || !hasChanged}>
            {lang.customerTriggeredEventsPage.save}
          </SaveButton>
        </FooterActionsBox>
      )}

      {customerTriggeredEventsModal.open && (
        <SimpleDialog
          open={customerTriggeredEventsModal.open}
          title={lang.customerTriggeredEventsModal.title}
          subTitle={
            transformDaypartComponentsPayload().data.length === 0
              ? lang.customerTriggeredEventsModal.subTitle
              : lang.customerTriggeredEventsModal.subTitleWithPublish
          }
          confirmText={lang.confirm}
          cancelText={lang.cancel}
          onClose={customerTriggeredEventsModal.onCloseModal}
          onClickCancel={customerTriggeredEventsModal.onCloseModal}
          onClickConfirm={async () =>
            await handleSaveConfirm((isUpdated?: boolean) => {
              customerTriggeredEventsModal.onCloseModal();
              if (isUpdated) {
                publicationModal.onOpenModal();
              }
            })
          }
          isLoading={isSaveLoading}
          modalSize="sm"
        />
      )}

      {publicationModal.open && (
        <PublicationModal
          open={publicationModal.open}
          onClose={publicationModal.onCloseModal}
        />
      )}
    </PageContainer>
  );
};
